import { useEffect, useState } from 'react';
import { Layout } from '../components/Layout.js';
import { apiGet, apiPost, ApiError } from '../api/client.js';
import {
  AlertTriangleIcon,
  CheckCircle2Icon,
  ExternalLinkIcon,
  RefreshCwIcon,
} from '../components/icons.js';

/**
 * /settings/connections — My Agent data-source connections (slice 1 of
 * docs/designs/2026-06-09-my-agent.md).
 *
 * Read-only status surface: the AI Agent Catalogue owns every OAuth flow
 * (connect, re-consent, disconnect), so all actions here deep-link to the
 * catalogue UI rather than proxying consent through our backend.
 */

interface GatewayConnectionSummary {
  id: string;
  connectorId: string;
  connectorType: string;
  displayLabel: string;
  status: string;
  scopesGranted: string[];
  externalAccountLabel: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

interface MeConnectionsResponse {
  catalogueUrl: string;
  credentialMissing: boolean;
  connectors: Array<{
    connectorType: string;
    label: string;
    description: string;
    connection: GatewayConnectionSummary | null;
  }>;
}

/** Graph scopes My Agent needs per M365 connector. CAUTION: a connection's
 *  scopesGranted is a connect-time snapshot — Entra app permissions added
 *  later (e.g. Calendars.Read, 2026-06-10) flow into live tokens WITHOUT
 *  appearing here. So this check can only flag scopes that were in the
 *  connector's original request set; never list later-added scopes or every
 *  healthy connection shows a false "reconnect" notice. */
const REQUIRED_SCOPES: Record<string, string[]> = {
  microsoft_outlook: ['Mail.Read', 'Files.Read.All', 'Sites.Read.All'],
  microsoft_teams: ['Chat.Read', 'ChannelMessage.Read.All', 'Team.ReadBasic.All'],
};

/** Plain-language "what we access" per connector — the privacy disclosure
 *  the design requires under each connector card. */
const ACCESS_DISCLOSURE: Record<string, string[]> = {
  microsoft_outlook: [
    'OneDrive and SharePoint files — searched live, never stored.',
    'Email subjects, senders, dates and previews — searched live; a full message is only read when you ask about it, and never stored.',
    'Your calendar — looked up live, never stored.',
  ],
  microsoft_teams: [
    'Teams chats — listed live; a thread is only read when you ask about it, and never stored.',
  ],
  jira_api: ['Tickets you can see in Jira, with your own project permissions.'],
  atlassian_rovo: ['Confluence pages and spaces your account can read.'],
  harvest: ['Your own time entries — looked up live, never stored.'],
};

/** Self-service deletion of everything My Agent stores for this user —
 *  the design's revoke-and-delete promise, user-triggerable. */
function PurgeSection(): JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<number | null>(null);

  const purge = (): void => {
    setBusy(true);
    apiPost<{ totalDeleted: number }>('/api/v1/my-agent/purge')
      .then((r) => setDone(r.totalDeleted))
      .catch(() => setDone(-1))
      .finally(() => {
        setBusy(false);
        setConfirming(false);
      });
  };

  return (
    <section className="mt-8 rounded-md border border-nebula-border-soft bg-nebula-surface p-5">
      <h2 className="ov-h-section">My Agent data</h2>
      <p className="ov-body mt-1 text-nebula-fg-2">
        My Agent keeps your conversation history (questions and answers). Your mail, files, chats
        and calendar are read live and never stored. You can delete everything it holds at any time.
      </p>
      {done !== null && (
        <p className={`ov-body mt-2 ${done >= 0 ? 'text-nebula-fg-1' : 'text-nebula-error'}`}>
          {done >= 0 ? `Deleted ${done} stored items.` : 'Deletion failed — try again.'}
        </p>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-3">
        {!confirming ? (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className="rounded-md border border-nebula-border bg-white px-3 py-1.5 font-ui text-xs font-medium text-nebula-error hover:bg-nebula-error-bg"
          >
            Delete my My Agent data
          </button>
        ) : (
          <>
            <span className="ov-meta text-nebula-fg-2">
              This permanently deletes all your My Agent conversations.
            </span>
            <button
              type="button"
              disabled={busy}
              onClick={purge}
              className="rounded-md bg-nebula-error px-3 py-1.5 font-ui text-xs font-medium text-white disabled:opacity-50"
            >
              {busy ? 'Deleting…' : 'Yes, delete everything'}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setConfirming(false)}
              className="rounded-md border border-nebula-border-soft bg-white px-3 py-1.5 font-ui text-xs font-medium text-nebula-fg-1 hover:bg-nebula-tint"
            >
              Cancel
            </button>
          </>
        )}
      </div>
    </section>
  );
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('en-GB');
}

function StatusBadge({ connection }: { connection: GatewayConnectionSummary | null }): JSX.Element {
  if (!connection) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-nebula-gray-5 px-2.5 py-1 font-ui text-xs text-nebula-fg-2">
        Not connected
      </span>
    );
  }
  if (connection.status === 'active') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-nebula-tint px-2.5 py-1 font-ui text-xs text-nebula-secondary">
        <CheckCircle2Icon className="h-3.5 w-3.5" />
        Connected
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-nebula-error-bg px-2.5 py-1 font-ui text-xs text-nebula-error">
      <AlertTriangleIcon className="h-3.5 w-3.5" />
      {connection.status}
    </span>
  );
}

export function SettingsConnectionsPage(): JSX.Element {
  const [data, setData] = useState<MeConnectionsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = (): void => {
    setLoading(true);
    setError(null);
    apiGet<MeConnectionsResponse>('/api/v1/me/connections')
      .then(setData)
      .catch((err: unknown) => {
        setError(
          err instanceof ApiError && err.status === 502
            ? 'The AI Agent Catalogue gateway is unreachable right now. Try again shortly.'
            : 'Could not load your connections.',
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  return (
    <Layout>
      <div className="mx-auto max-w-3xl px-6 py-6">
        <header className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="ov-h-page">Connections</h1>
            <p className="ov-body mt-1 text-nebula-fg-3">
              Data sources My Agent can use on your behalf. Each connection uses your own account —
              the agent only ever sees what you can see.
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-nebula-border-soft bg-white px-3 py-1.5 font-ui text-xs font-medium text-nebula-fg-1 hover:bg-nebula-tint"
          >
            <RefreshCwIcon className="h-3.5 w-3.5" />
            Refresh
          </button>
        </header>

        {loading && <p className="ov-body text-nebula-fg-3">Loading your connections…</p>}

        {!loading && error && (
          <div className="rounded-md border border-nebula-border-soft bg-nebula-error-bg px-4 py-3">
            <p className="ov-body text-nebula-error">{error}</p>
          </div>
        )}

        {!loading && !error && data && (
          <>
            {data.credentialMissing && (
              <div className="mb-4 flex items-start gap-3 rounded-md border border-nebula-border-soft bg-nebula-tint px-4 py-3">
                <AlertTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-nebula-secondary" />
                <p className="ov-body text-nebula-fg-1">
                  Your session predates the connections feature. Sign out and sign in again so the
                  app can check your connection status.
                </p>
              </div>
            )}

            <div className="space-y-4">
              {data.connectors.map((c) => {
                const missingScopes = c.connection
                  ? (REQUIRED_SCOPES[c.connectorType] ?? []).filter(
                      (s) => !c.connection!.scopesGranted.includes(s),
                    )
                  : [];
                return (
                  <section
                    key={c.connectorType}
                    className="rounded-md border border-nebula-border-soft bg-nebula-surface p-5 shadow-card"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h2 className="ov-h-section">{c.label}</h2>
                        <p className="ov-body mt-0.5 text-nebula-fg-2">{c.description}</p>
                      </div>
                      <StatusBadge connection={c.connection} />
                    </div>

                    {c.connection && (
                      <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-2 md:grid-cols-2">
                        <div>
                          <dt className="ov-meta-upper">Connected</dt>
                          <dd className="ov-body mt-0.5">{fmtDate(c.connection.createdAt)}</dd>
                        </div>
                        <div>
                          <dt className="ov-meta-upper">Last updated</dt>
                          <dd className="ov-body mt-0.5">{fmtDate(c.connection.updatedAt)}</dd>
                        </div>
                      </dl>
                    )}

                    {missingScopes.length > 0 && (
                      <div className="mt-3 rounded-md border border-nebula-border-soft bg-nebula-tint px-3 py-2.5">
                        <p className="ov-meta text-nebula-fg-1">
                          This connection predates some My Agent sources. Reconnect it in the
                          catalogue to grant:
                        </p>
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {missingScopes.map((s) => (
                            <span
                              key={s}
                              className="rounded-full bg-white px-2 py-0.5 font-mono text-[11px] text-nebula-fg-2"
                            >
                              {s}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <details className="mt-3">
                      <summary className="ov-meta cursor-pointer select-none text-nebula-fg-2 hover:text-nebula-fg-1">
                        What we access
                      </summary>
                      <ul className="ov-meta mt-1.5 list-disc space-y-1 pl-5 text-nebula-fg-2">
                        {(ACCESS_DISCLOSURE[c.connectorType] ?? []).map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </ul>
                    </details>

                    <div className="mt-4">
                      <a
                        href={data.catalogueUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-md bg-nebula-secondary px-3 py-1.5 font-ui text-xs font-medium text-white hover:bg-nebula-primary"
                      >
                        {c.connection
                          ? 'Manage in AI Agent Catalogue'
                          : 'Connect in AI Agent Catalogue'}
                        <ExternalLinkIcon className="h-3.5 w-3.5" />
                      </a>
                    </div>
                  </section>
                );
              })}
            </div>

            <p className="ov-meta mt-6 text-nebula-fg-3">
              Connecting, re-consenting and disconnecting all happen in the AI Agent Catalogue. My
              Agent reads these sources live and keeps no copy of them — disconnecting one stops
              future reads immediately. To remove the conversation history it does keep, use “Delete
              my My Agent data” below.
            </p>

            <PurgeSection />
          </>
        )}
      </div>
    </Layout>
  );
}
